import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import {
    Container, Row, Col, Card, CardBody,
    Form, FormGroup, Label, Input, Button, Spinner, Alert
} from "reactstrap";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import {
    fetchPublicSongFlow,
    subscribeToPublicSongFlow
} from "../../components/helpers/PublicCuratedSongFlowHelpers";
import { debugLog } from "../../utils/debug";

function PublicSongFlowSubscribe() {
    const { slug } = useParams();
    const { executeRecaptcha } = useGoogleReCaptcha();

    const [songFlow, setSongFlow] = useState(null);
    const [loadError, setLoadError] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [honeypot, setHoneypot] = useState("");
    const [emailError, setEmailError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [submitError, setSubmitError] = useState("");

    useEffect(() => {
        fetchPublicSongFlow(slug).then(data => {
            if (!data) setLoadError(true);
            else setSongFlow(data);
            setIsLoading(false);
        });
    }, [slug]);

    const validateEmail = (value) => {
        if (!value || !value.trim()) return "Email address is required.";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) return "Please enter a valid email address.";
        return "";
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (honeypot) return;
        const err = validateEmail(email);
        if (err) { setEmailError(err); return; }
        setEmailError("");
        setIsSubmitting(true);
        setSubmitError("");

        const recaptchaToken = executeRecaptcha ? await executeRecaptcha("subscribe_song_flow") : null;
        const result = await subscribeToPublicSongFlow(slug, email.trim(), name.trim() || null, recaptchaToken, honeypot);
        setIsSubmitting(false);

        if (result && result.success) {
            if (typeof window.gtag === "function") {
                window.gtag("event", "public_song_flow_subscribe_complete", { song_flow_slug: slug });
            }
            setSubmitted(true);
        } else {
            setSubmitError("Something went wrong. Please try again.");
        }
    };

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
                <Spinner color="primary" />
            </div>
        );
    }

    if (loadError) {
        return (
            <div className="position-relative">
                <section className="section section-lg section-shaped pb-250">
                    <div className="shape shape-style-1 shape-default">
                        <span /><span /><span /><span /><span />
                    </div>
                    <Container className="py-lg-md d-flex">
                        <div className="col px-0">
                            <Row>
                                <Col lg="8">
                                    <h1 className="display-3 text-white">Song-flow not found</h1>
                                    <p className="lead text-white">This link may have expired or the song-flow is no longer available.</p>
                                </Col>
                            </Row>
                        </div>
                    </Container>
                    <div className="separator separator-bottom separator-skew">
                        <svg xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" viewBox="0 0 2560 100">
                            <polygon className="fill-white" points="2560 0 2560 100 0 100" />
                        </svg>
                    </div>
                </section>
            </div>
        );
    }

    return (
        <div className="position-relative">
            <section className="section section-lg section-shaped pb-250">
                <div className="shape shape-style-1 shape-default">
                    <span /><span /><span /><span /><span /><span /><span /><span /><span />
                </div>
                <Container className="py-lg-md d-flex">
                    <div className="col px-0">
                        <Row>
                            <Col lg="8">
                                <h1 className="display-3 text-white">Subscribe</h1>
                                <p className="lead text-white">
                                    Receive curated worship song-flows from The Worship Bible — free, no account needed.
                                </p>
                            </Col>
                        </Row>
                    </div>
                </Container>
                <div className="separator separator-bottom separator-skew">
                    <svg xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" viewBox="0 0 2560 100">
                        <polygon className="fill-white" points="2560 0 2560 100 0 100" />
                    </svg>
                </div>
            </section>

            <section className="section section-lg pt-lg-0 mt--200">
                <Container>
                    <Row className="justify-content-center">
                        <Col lg="6">
                            <Card className="shadow border-0">
                                <CardBody className="py-5 px-5">
                                    {songFlow && (
                                        <p className="text-muted mb-3" style={{ fontSize: "0.9rem" }}>
                                            Signing up for: <strong>{songFlow.name}</strong>
                                        </p>
                                    )}

                                    {submitted ? (
                                        <div>
                                            <Alert color="success">
                                                <strong>You&rsquo;re subscribed!</strong> Check your inbox for a confirmation email.
                                            </Alert>
                                            <Link to={`/song-flows/${slug}`} className="btn btn-primary mt-2">
                                                Back to song-flow
                                            </Link>
                                        </div>
                                    ) : (
                                        <Form onSubmit={handleSubmit}>
                                            {/* Honeypot — hidden from real users, bots fill it in */}
                                            <div style={{ position: "absolute", left: "-9999px", top: "-9999px", opacity: 0, height: 0, overflow: "hidden" }} aria-hidden="true">
                                                <label htmlFor="website">Website</label>
                                                <Input id="website" type="text" name="website" value={honeypot} onChange={e => setHoneypot(e.target.value)} tabIndex="-1" autoComplete="off" />
                                            </div>
                                            <FormGroup>
                                                <Label for="name">Name <span className="text-muted" style={{ fontSize: "0.85rem" }}>(optional)</span></Label>
                                                <Input
                                                    id="name"
                                                    type="text"
                                                    placeholder="Your name"
                                                    value={name}
                                                    onChange={e => setName(e.target.value)}
                                                    disabled={isSubmitting}
                                                />
                                            </FormGroup>
                                            <FormGroup>
                                                <Label for="email">Email address <span className="text-danger">*</span></Label>
                                                <Input
                                                    id="email"
                                                    type="email"
                                                    placeholder="you@example.com"
                                                    value={email}
                                                    onChange={e => { setEmail(e.target.value); setEmailError(""); }}
                                                    invalid={!!emailError}
                                                    disabled={isSubmitting}
                                                />
                                                {emailError && (
                                                    <div className="invalid-feedback d-block">{emailError}</div>
                                                )}
                                            </FormGroup>

                                            {submitError && <Alert color="danger">{submitError}</Alert>}

                                            <Button color="primary" type="submit" disabled={isSubmitting} block>
                                                {isSubmitting ? <Spinner size="sm" /> : "Subscribe free"}
                                            </Button>

                                            <p className="text-muted mt-3 mb-0" style={{ fontSize: "0.8rem" }}>
                                                You can unsubscribe at any time. We will never spam you or share your details.
                                            </p>
                                        </Form>
                                    )}

                                    <hr className="my-4" />
                                    <p className="text-muted mb-0" style={{ fontSize: "0.85rem" }}>
                                        Want to build your own worship song-flows?{" "}
                                        <Link to="/register/choose-your-plan">Register</Link> for a full account.
                                    </p>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </section>
        </div>
    );
}

export default PublicSongFlowSubscribe;
